import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Button, Icon, Stack, Text } from '@/design-system'
import { CurrencyBadge } from '@/components/CurrencyBadge'
import { FlowLayout } from '@/app/layouts/FlowLayout'
import { cn } from '@/lib/cn'
import type { CurrencyCode } from '@/lib/money'
import { ACCOUNT_OPTIONS } from './onboarding-data'
import styles from './SelectCard.module.css'

export function OnboardingAccounts() {
  const navigate = useNavigate()
  const [selected, setSelected] = useState<readonly CurrencyCode[]>(['USD', 'MXN'])

  const toggle = (currency: CurrencyCode) =>
    setSelected((current) =>
      current.includes(currency)
        ? current.filter((item) => item !== currency)
        : [...current, currency],
    )

  return (
    <FlowLayout
      step={1}
      totalSteps={3}
      back="/signup"
      footer={
        <>
          <Button size="lg" block disabled={selected.length === 0} onClick={() => navigate('/onboarding/card')}>
            Continue with {selected.length} {selected.length === 1 ? 'account' : 'accounts'}
          </Button>
          <Text variant="eyebrow" tone="secondary">
            Free to open. Each one starts with a $50 deposit that stays yours.
          </Text>
        </>
      }
    >
      <Stack gap={6}>
        <Stack gap={2}>
          <Text as="h1" variant="title" weight="bold">
            Which accounts do you want?
          </Text>
          <Text variant="body" tone="secondary">
            Real local details in your name, not a pooled account. You can add more at any time.
          </Text>
        </Stack>

        <div className={styles.options}>
          {ACCOUNT_OPTIONS.map((option) => {
            const isOn = selected.includes(option.currency)
            return (
              <button
                key={option.currency}
                type="button"
                className={cn(styles.option, isOn && styles.selected)}
                onClick={() => toggle(option.currency)}
                aria-pressed={isOn}
              >
                <CurrencyBadge currency={option.currency} size={40} />
                <Stack gap={1}>
                  <Text variant="caption" weight="semibold">
                    {option.label}
                  </Text>
                  <Text variant="eyebrow" tone="secondary">
                    {option.rail}
                  </Text>
                  <Text variant="eyebrow" tone="secondary">
                    {option.note}
                  </Text>
                </Stack>
                <Icon
                  name={isOn ? 'check_circle' : 'radio_button_unchecked'}
                  size={20}
                  filled={isOn}
                  className={cn(styles.check, isOn && styles.checked)}
                />
              </button>
            )
          })}
        </div>
      </Stack>
    </FlowLayout>
  )
}
